import * as React from 'react';
import { useState, SyntheticEvent, useEffect } from 'react';
import {
  Tabs,
  Tab,
  Typography,
  Box,
  Select,
  MenuItem,
  SelectChangeEvent,
  IconButton,
  Modal,
} from '@mui/material';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from '../../store';
import {
  setAlertsRefreshRate,
  setAssetsSummaryVisibility,
  setAlertsSummaryVisibility,
  setDeviceStatusSummaryVisibility,
  setSelectedLanguage,
  setTicketsSummaryVisibility,
  setHealthSummaryVisibility,
} from '../../store/UserSettings/UserSettingsActions';
import { Dispatch } from 'redux';
import CloseIcon from '@material-ui/icons/Close';
import IconAlert from '@material-ui/icons/ReportProblemOutlined';
import IconSettingsOutlined from '@material-ui/icons/SettingsOutlined';
import ReferenceWeightsManagement from '../../components/ReferenceWeightsManagement/ReferenceWeightsManagement';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { WidgetsDroppableList } from '../DroppableList/WidgetsDroppableList';
// Constants
import {
  ALERTS_REFRESH_RATE,
  ONE_MINUTE,
  FIVE_MINUTES,
  FIFTEEN_MINUTES,
  THIRTY_MINUTES,
} from '../../utils/constants';
import { GetString } from '../StringResourceManager';
import StringResources from '../../utils/stringResources';
import { WidgetsState } from '../../Interfaces/Overview/WidgetsState';
import { AccessControl } from '../../Interfaces/AccessControl';

// Styles
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    box: {
      display: 'flex',
      width: 500,
      height: 600,
      flexDirection: 'column',
      borderRadius: 5,
      backgroundColor: '#fff',
      padding: 10,
    },
    header: {
      flex: 0.1,
      display: 'flex',
      flexDirection: 'row',
    },
    title: {
      alignItems: 'center',
      justifyContent: 'center',
      color: '#23496B',
      display: 'flex',
      flex: 0.3,
    },
    headerSpace: {
      display: 'flex',
      flex: 0.7,
      alignItems: 'center',
      color: '#23496B',
      justifyContent: 'flex-end',
      marginRight: 20,
    },
    tabsContainer: {
      flex: 0.9,
      display: 'flex',
      flexDirection: 'row',
    },
    tabs: {
      '& .MuiTabs-flexContainerVertical': {
        padding: 5,
      },
      '& .MuiTabs-indicator': {
        display: 'none',
        color: '#fff',
      },
      '& .Mui-selected': {
        backgroundColor: '#23496B',
        borderRadius: 5,
        color: '#fff',
      },
      flex: 0.3,
      display: 'flex',
    },
    tab: {
      '& .MuiTab-root': {
        height: 40,
      },
      '&:hover': {
        color: '#fff',
        backgroundColor: '#23496B',
        borderRadius: 5,
      },
      display: 'flex',
      height: 40,
    },
    tabPanel: {
      display: 'flex',
      flex: 0.7,
      padding: 5,
    },
    tabPanelLabel: {
      marginBottom: 10,
    },
    select: {
      minHeight: 40,
    },
    settingsModal: {
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: theme.shape.borderRadius,
    },
  })
);

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface SettingsVerticalTabsProps {
  visible: boolean;
  handleClose: () => void;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ ml: 2 }}>{children}</Box>}
    </div>
  );
}

const SettingsVerticalTabs: React.FC<SettingsVerticalTabsProps> = ({
  visible,
  handleClose,
}) => {
  const classes = useStyles();
  const [value, setValue] = useState(0);

  const dispatch: Dispatch<any> = useDispatch();

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const accessControl: AccessControl | undefined = useSelector(
    (state: ApplicationState) =>
      state.userSessionReducer.sessionInfo?.AccessControl
  );

  const refreshRate: number = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.refreshRate
  );

  const selectedRefreshedRate: string = refreshRate.toString();

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage
  );

  const handleRefreshRateChange = (event: SelectChangeEvent) => {
    const newValue: number = Number(event.target.value);
    setAlertsRefreshRate(dispatch, newValue);
  };

  const handleSelectedLanguageChange = (event: SelectChangeEvent) => {
    setSelectedLanguage(dispatch, event.target.value);
  };

  const showAssetsSummary: boolean | undefined = useSelector(
    (state: ApplicationState) =>
      state.userSettingsReducer.widgetsState.find(
        (a: WidgetsState) => a.id == 1
      )?.visible
  );

  const showAlertsSummary: boolean | undefined = useSelector(
    (state: ApplicationState) =>
      state.userSettingsReducer.widgetsState.find(
        (a: WidgetsState) => a.id == 4
      )?.visible
  );

  const showDeviceStatusSummary: boolean | undefined = useSelector(
    (state: ApplicationState) =>
      state.userSettingsReducer.widgetsState.find(
        (a: WidgetsState) => a.id == 2
      )?.visible
  );

  const showTicketsSummary: boolean | undefined = useSelector(
    (state: ApplicationState) =>
      state.userSettingsReducer.widgetsState.find(
        (a: WidgetsState) => a.id == 5
      )?.visible
  );

  const showHealthSummary: boolean | undefined = useSelector(
    (state: ApplicationState) =>
      state.userSettingsReducer.widgetsState.find(
        (a: WidgetsState) => a.id == 3
      )?.visible
  );

  const [showWidgetsArrangementControl, setShowWidgetsArrangementControl] =
    useState<boolean>(true);

  useEffect(() => {
    setAlertsSummaryVisibility(
      dispatch,
      (!accessControl ||
        !accessControl.Alerts ||
        accessControl.Alerts.ShowMenu) &&
        showAlertsSummary
    );

    setAssetsSummaryVisibility(
      dispatch,
      (!accessControl ||
        !accessControl.Assets ||
        accessControl.Assets.ShowMenu) &&
        showAssetsSummary
    );

    setDeviceStatusSummaryVisibility(
      dispatch,
      (!accessControl ||
        !accessControl.Devices ||
        accessControl.Devices.ShowMenu) &&
        showDeviceStatusSummary
    );

    setTicketsSummaryVisibility(
      dispatch,
      (!accessControl ||
        !accessControl.Tickets ||
        accessControl.Tickets.ShowMenu) &&
        showTicketsSummary
    );

    setHealthSummaryVisibility(
      dispatch,
      (!accessControl ||
        !accessControl.Health ||
        accessControl.Health.ShowMenu) &&
        showHealthSummary
    );
  }, []);

  useEffect(() => {
    let timeoutId: any;
    if (!showWidgetsArrangementControl) {
      timeoutId = setTimeout(() => {
        setShowWidgetsArrangementControl(true);
      }, 100);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [showWidgetsArrangementControl]);

  const handleAssetsSummaryVisibility = () => {
    setAssetsSummaryVisibility(dispatch, !showAssetsSummary);
    refreshWidgetsArrangementControl();
  };

  const handleAlertsSummaryVisibility = () => {
    setAlertsSummaryVisibility(dispatch, !showAlertsSummary);
    refreshWidgetsArrangementControl();
  };

  const handleDeviceStatusSummaryVisibility = () => {
    setDeviceStatusSummaryVisibility(dispatch, !showDeviceStatusSummary);
    refreshWidgetsArrangementControl();
  };

  const handleTicketsSummaryVisibility = () => {
    setTicketsSummaryVisibility(dispatch, !showTicketsSummary);
    refreshWidgetsArrangementControl();
  };

  const handleHealthSummaryVisibility = () => {
    setHealthSummaryVisibility(dispatch, !showHealthSummary);
    refreshWidgetsArrangementControl();
  };

  const refreshWidgetsArrangementControl = () => {
    setShowWidgetsArrangementControl(false);
  };

  return (
    <Modal
      className={classes.settingsModal}
      open={visible}
      onClose={() => {}}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box className={classes.box}>
        <div className={classes.header}>
          <div className={classes.title}>
            <Typography variant="h6" color="textSecondary">
              {GetString('PAGE_TITLE_SETTINGS')}
            </Typography>
          </div>
          <div className={classes.headerSpace}>
            <IconButton
              aria-label="close"
              color="primary"
              onClick={handleClose}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </div>

        <div className={classes.tabsContainer}>
          <Tabs
            orientation="vertical"
            variant="scrollable"
            value={value}
            onChange={handleChange}
            aria-label="settings vertical tabs"
            className={classes.tabs}
            textColor={'inherit'}
          >
            <Tab
              label={GetString('OVERVIEW_SECTION')}
              icon={<IconSettingsOutlined />}
              iconPosition={'start'}
              className={classes.tab}
            />
            <Tab
              label={GetString('PAGE_TITLE_ALERTS')}
              icon={<IconAlert />}
              iconPosition={'start'}
              className={classes.tab}
            />
            <Tab
              label={GetString('WEIGHTS')}
              icon={<IconSettingsOutlined />}
              iconPosition={'start'}
              className={classes.tab}
            />
            <Tab
              label={GetString('LANGUAGE_LABEL')}
              icon={<IconSettingsOutlined />}
              iconPosition={'start'}
              className={classes.tab}
            />
          </Tabs>
          <div className={classes.tabPanel}>
            <TabPanel value={value} index={0}>
              <Typography variant="body1" color="textSecondary">
                {GetString('ARRANGEMENT')}
              </Typography>
              {showWidgetsArrangementControl ? <WidgetsDroppableList /> : null}
              <FormControlLabel
                control={
                  <Checkbox
                    name="checkAlertsSummary"
                    color="primary"
                    checked={showAlertsSummary}
                    onChange={handleAlertsSummaryVisibility}
                  />
                }
                label={GetString('SHOW_ALERTS_SUMMARY')}
                disabled={
                  accessControl?.Alerts && !accessControl.Alerts.ShowMenu
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="checkAssetsSummary"
                    color="primary"
                    checked={showAssetsSummary}
                    onChange={handleAssetsSummaryVisibility}
                  />
                }
                label={GetString('SHOW_ASSETS_SUMMARY')}
                disabled={
                  accessControl?.Assets && !accessControl.Assets.ShowMenu
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="checkDeviceStatusSummary"
                    color="primary"
                    checked={showDeviceStatusSummary}
                    onChange={handleDeviceStatusSummaryVisibility}
                  />
                }
                label={GetString('SHOW_DEVICE_STATUS_SUMMARY')}
                disabled={
                  accessControl?.Devices && !accessControl.Devices.ShowMenu
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="checkTicketsSummary"
                    color="primary"
                    checked={showTicketsSummary}
                    onChange={handleTicketsSummaryVisibility}
                  />
                }
                label={GetString('SHOW_TICKETS_SUMMARY')}
                disabled={
                  accessControl?.Tickets && !accessControl.Tickets.ShowMenu
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="checkHealthSummary"
                    color="primary"
                    checked={showHealthSummary}
                    onChange={handleHealthSummaryVisibility}
                  />
                }
                label={GetString('SHOW_HEALTH_SUMMARY')}
                disabled={
                  accessControl?.Health && !accessControl.Health.ShowMenu
                }
              />
            </TabPanel>
            <TabPanel value={value} index={1}>
              <div className={classes.tabPanelLabel}>
                <Typography variant="body2" color="textSecondary">
                  {GetString('REFRESH_RATE')}
                </Typography>
              </div>
              <Select
                value={selectedRefreshedRate}
                onChange={handleRefreshRateChange}
                inputProps={{ 'aria-label': 'Without label' }}
              >
                <MenuItem value={ALERTS_REFRESH_RATE.DISABLED}>
                  {GetString('REFRESH_DISABLED')}
                </MenuItem>
                <MenuItem value={ALERTS_REFRESH_RATE.ONE_MINUTE}>
                  {ONE_MINUTE}
                </MenuItem>
                <MenuItem value={ALERTS_REFRESH_RATE.FIVE_MINUTES}>
                  {FIVE_MINUTES}
                </MenuItem>
                <MenuItem value={ALERTS_REFRESH_RATE.FIFTEEN_MINUTES}>
                  {FIFTEEN_MINUTES}
                </MenuItem>
                <MenuItem value={ALERTS_REFRESH_RATE.THIRTY_MINUTES}>
                  {THIRTY_MINUTES}
                </MenuItem>
                <MenuItem value={ALERTS_REFRESH_RATE.ONE_HOUR}>
                  {GetString('ONE_HOUR_STR')}
                </MenuItem>
              </Select>
            </TabPanel>
            <TabPanel value={value} index={2}>
              <ReferenceWeightsManagement />
            </TabPanel>
            <TabPanel value={value} index={3}>
              <div className={classes.tabPanelLabel}>
                <Typography variant="body2" color="textSecondary">
                  {GetString('LANGUAGE_LABEL')}
                </Typography>
              </div>
              <Select
                value={selectedLanguage}
                onChange={handleSelectedLanguageChange}
                inputProps={{ 'aria-label': 'Without label' }}
              >
                <MenuItem value={StringResources.English.ENGLISH_OPTION}>
                  {GetString('ENGLISH_OPTION')}
                </MenuItem>
                <MenuItem value={StringResources.English.FRENCH_OPTION}>
                  {GetString('FRENCH_OPTION')}
                </MenuItem>
              </Select>
            </TabPanel>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default SettingsVerticalTabs;
